/**
 * Pure resolver: given a list of installed profiles and a moment in time,
 * pick the profile + active schedule period that wins for a connector.
 *
 * Mirrors legacy/evse2/js/ocpp_chargepoint.js → resolveActiveProfile.
 * No I/O, no clock — `nowMs` is an input.
 */
import { ProfilePurpose, type ChargingRateUnit, type ChargingSchedulePeriod } from './types';
import type { ChargingProfileSnapshot } from './ChargingProfile';

export const DEFAULT_PHASE_VOLTAGE = 230;
export const DEFAULT_NUMBER_PHASES = 3;

export interface ResolveInput {
  readonly profiles: readonly ChargingProfileSnapshot[];
  readonly connectorId: number;
  readonly txId: number | null;
  readonly nowMs: number;
  readonly txStartedAtMs: number | null;
}

export interface ResolvedProfile {
  readonly profile: ChargingProfileSnapshot;
  readonly period: ChargingSchedulePeriod;
  readonly offsetSec: number;
  /** Limit converted to watts using DEFAULT_PHASE_VOLTAGE for 'A' schedules. */
  readonly limitW: number;
}

export function resolveActiveProfile(input: ResolveInput): ResolvedProfile | null {
  const candidates = candidateProfiles(input);
  const live: ResolvedProfile[] = [];
  for (const p of candidates) {
    const offsetSec = scheduleOffsetSec(p, input.nowMs, input.txStartedAtMs);
    if (offsetSec < 0) continue;
    const period = findActivePeriod(
      p.schedule.chargingSchedulePeriod,
      offsetSec,
      p.schedule.duration,
    );
    if (!period) continue;
    const limitW = chargingRateToWatts(
      period.limit,
      p.schedule.chargingRateUnit,
      period.numberPhases,
    );
    live.push({ profile: p, period, offsetSec, limitW });
  }
  if (live.length === 0) return null;
  live.sort((a, b) => {
    const sd = b.profile.stackLevel - a.profile.stackLevel;
    if (sd !== 0) return sd;
    return purposeWeight(b.profile.purpose) - purposeWeight(a.profile.purpose);
  });
  return live[0] ?? null;
}

/** Apply candidate-filtering rules (connector scope, validity window, TxProfile↔txId). */
export function candidateProfiles(input: ResolveInput): readonly ChargingProfileSnapshot[] {
  const { profiles, connectorId, txId, nowMs } = input;
  return profiles.filter((p) => {
    if (p.connectorId !== 0 && p.connectorId !== connectorId) return false;
    if (p.validFrom !== undefined && nowMs < p.validFrom) return false;
    if (p.validTo !== undefined && nowMs > p.validTo) return false;
    if (p.purpose === ProfilePurpose.TxProfile) {
      if (txId === null || p.transactionId !== txId) return false;
    }
    return true;
  });
}

/**
 * Find the period applicable at `tSec` seconds into a schedule. The "active"
 * period is the one with the largest startPeriod ≤ tSec; OCPP requires the
 * periods sorted ascending — we sort defensively.
 */
export function findActivePeriod(
  periods: readonly ChargingSchedulePeriod[],
  tSec: number,
  durationSec?: number,
): ChargingSchedulePeriod | null {
  if (periods.length === 0) return null;
  if (durationSec !== undefined && tSec >= durationSec) return null;
  const sorted = [...periods].sort((a, b) => a.startPeriod - b.startPeriod);
  const first = sorted[0];
  if (first === undefined || tSec < first.startPeriod) return null;
  let active: ChargingSchedulePeriod | null = null;
  for (const p of sorted) {
    if (p.startPeriod <= tSec) active = p;
    else break;
  }
  return active;
}

/**
 * Offset (seconds) from the profile's effective start to `nowMs`. Negative
 * values mean "not yet active" — the resolver skips those profiles.
 */
export function scheduleOffsetSec(
  profile: ChargingProfileSnapshot,
  nowMs: number,
  relativeAnchorMs: number | null,
): number {
  const sched = profile.schedule;
  if (profile.kind === 'Relative') {
    if (relativeAnchorMs === null || !Number.isFinite(relativeAnchorMs)) return -1;
    return Math.floor((nowMs - relativeAnchorMs) / 1000);
  }
  if (sched.startSchedule === undefined || !Number.isFinite(sched.startSchedule)) return -1;
  const startMs = sched.startSchedule;
  if (profile.kind === 'Absolute') {
    return Math.floor((nowMs - startMs) / 1000);
  }
  if (profile.kind === 'Recurring') {
    const sinceStart = nowMs - startMs;
    if (sinceStart < 0) return -1;
    if (profile.recurrencyKind === 'Daily') {
      const dayMs = 24 * 3600 * 1000;
      return Math.floor((sinceStart % dayMs) / 1000);
    }
    if (profile.recurrencyKind === 'Weekly') {
      const weekMs = 7 * 24 * 3600 * 1000;
      return Math.floor((sinceStart % weekMs) / 1000);
    }
  }
  return -1;
}

/** Hierarchy weight for tie-breaking at equal stackLevel. */
export function purposeWeight(purpose: ChargingProfileSnapshot['purpose']): number {
  if (purpose === ProfilePurpose.TxProfile) return 3;
  if (purpose === ProfilePurpose.TxDefaultProfile) return 2;
  if (purpose === ProfilePurpose.ChargePointMaxProfile) return 1;
  return 0;
}

/**
 * Convert a schedule limit to watts. 'W' is a no-op; 'A' multiplies by
 * voltage * phases (period.numberPhases overrides the default).
 */
export function chargingRateToWatts(
  limit: number,
  unit: ChargingRateUnit,
  numberPhases: number | undefined,
  phaseVoltage: number = DEFAULT_PHASE_VOLTAGE,
): number {
  const n = numberPhases && numberPhases > 0 ? numberPhases : DEFAULT_NUMBER_PHASES;
  if (unit === 'A') return limit * phaseVoltage * n;
  return limit;
}

export function chargingRateToAmps(
  watts: number,
  numberPhases: number | undefined,
  phaseVoltage: number = DEFAULT_PHASE_VOLTAGE,
): number {
  const n = numberPhases && numberPhases > 0 ? numberPhases : DEFAULT_NUMBER_PHASES;
  return watts / (phaseVoltage * n);
}

// ── Composite schedule ──────────────────────────────────────────────────────

export interface CompositePeriod {
  /** Seconds from the composite schedule start (= nowMs). */
  readonly startPeriod: number;
  /** Limit in the requested `rateUnit`. */
  readonly limit: number;
}

export interface CompositeScheduleResult {
  readonly chargingRateUnit: ChargingRateUnit;
  readonly periods: readonly CompositePeriod[];
}

export interface CompositeInput extends ResolveInput {
  /** Composite window length, seconds from nowMs. */
  readonly durationSec: number;
  /** Output unit. 'A' is derived from watts with DEFAULT phases/voltage. */
  readonly rateUnit: ChargingRateUnit;
}

/**
 * Limit (watts) imposed by the highest-stackLevel ACTIVE profile of `purpose`
 * at composite-time `tc`. Returns null when no profile of that purpose is
 * active there. (Within a purpose the higher stackLevel wins — not the min.)
 */
function purposeLimitWAt(
  candidates: readonly ChargingProfileSnapshot[],
  purpose: ProfilePurpose,
  tc: number,
  nowMs: number,
  txStartedAtMs: number | null,
): number | null {
  let best: number | null = null;
  let bestStack = -Infinity;
  for (const p of candidates) {
    if (p.purpose !== purpose) continue;
    const offset = scheduleOffsetSec(p, nowMs, txStartedAtMs);
    if (offset < 0) continue;
    const period = findActivePeriod(p.schedule.chargingSchedulePeriod, offset + tc, p.schedule.duration);
    if (!period) continue;
    if (p.stackLevel >= bestStack) {
      bestStack = p.stackLevel;
      best = chargingRateToWatts(period.limit, p.schedule.chargingRateUnit, period.numberPhases);
    }
  }
  return best;
}

/**
 * Effective limit (watts) at composite-time `tc`: the operating profile
 * (TxProfile if a tx is running, else TxDefaultProfile) capped by
 * ChargePointMaxProfile. null when nothing constrains charging at `tc`.
 */
function effectiveLimitWAt(
  candidates: readonly ChargingProfileSnapshot[],
  tc: number,
  input: CompositeInput,
): number | null {
  const cpMax = purposeLimitWAt(candidates, ProfilePurpose.ChargePointMaxProfile, tc, input.nowMs, input.txStartedAtMs);
  let op =
    input.txId !== null
      ? purposeLimitWAt(candidates, ProfilePurpose.TxProfile, tc, input.nowMs, input.txStartedAtMs)
      : null;
  if (op === null) {
    op = purposeLimitWAt(candidates, ProfilePurpose.TxDefaultProfile, tc, input.nowMs, input.txStartedAtMs);
  }
  const parts = [cpMax, op].filter((x): x is number => x !== null);
  return parts.length === 0 ? null : Math.min(...parts);
}

/**
 * Flatten all installed profiles into a single composite schedule over
 * [0, durationSec): at each period boundary the effective limit is
 * `min(ChargePointMax, winningTxLevelProfile)` — see `effectiveLimitWAt`.
 * Consecutive equal limits are coalesced. Returns null (→ Rejected) when no
 * profile is active anywhere in the window.
 *
 * Simplifications (documented): profiles are anchored at nowMs like
 * `resolveActiveProfile` (a profile not yet active at nowMs — incl. Relative
 * without a tx anchor — is skipped); a mid-window Recurring cycle re-entry is
 * not modelled; an 'A' output unit is derived from watts with DEFAULT
 * phases/voltage.
 */
export function computeCompositeSchedule(input: CompositeInput): CompositeScheduleResult | null {
  const candidates = candidateProfiles(input);
  const duration = input.durationSec;

  // Boundary times (composite seconds) where the effective limit can change:
  // each profile's period starts and its schedule end, mapped to composite time.
  const bounds = new Set<number>([0]);
  for (const p of candidates) {
    const offset = scheduleOffsetSec(p, input.nowMs, input.txStartedAtMs);
    if (offset < 0) continue;
    for (const per of p.schedule.chargingSchedulePeriod) {
      const tc = per.startPeriod - offset;
      if (tc > 0 && tc < duration) bounds.add(tc);
    }
    if (p.schedule.duration !== undefined) {
      const end = p.schedule.duration - offset;
      if (end > 0 && end < duration) bounds.add(end);
    }
  }

  const periods: CompositePeriod[] = [];
  let lastLimit: number | null = null;
  for (const tc of [...bounds].sort((a, b) => a - b)) {
    const effW = effectiveLimitWAt(candidates, tc, input);
    if (effW === null) continue;
    const limit = input.rateUnit === 'A' ? chargingRateToAmps(effW, undefined) : effW;
    if (lastLimit === null || limit !== lastLimit) {
      periods.push({ startPeriod: tc, limit });
      lastLimit = limit;
    }
  }
  if (periods.length === 0) return null;
  return { chargingRateUnit: input.rateUnit, periods };
}
